// @flow
import React from 'react';
import InputMask from 'react-input-mask';
import { withTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';

type Props = {
  value: string,
  onChange: (value: string, countryCode: string, isValid: boolean) => void,
  placeholder?: string,
  className?: string,
  t: Function
};

type State = {
  countryCode: string,
  phoneNumber: string
};

const COUNTRIES = [
  { code: '+33', name: 'France', flag: '🇫🇷', mask: '09 99 99 99 99', iso: 'FR' },
  { code: '+41', name: 'Switzerland', flag: '🇨🇭', mask: '99 999 99 99', iso: 'CH' }
];

class CountryPhoneInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // Detect default country from navigator.language
    const defaultCountryCode = this.detectDefaultCountry();

    // Parse existing value if provided
    let initialCountryCode = defaultCountryCode;
    let initialPhoneNumber = '';

    if (props.value) {
      const parsed = this.parsePhoneValue(props.value);
      initialCountryCode = parsed.countryCode || defaultCountryCode;
      initialPhoneNumber = parsed.phoneNumber;
    }

    this.state = {
      countryCode: initialCountryCode,
      phoneNumber: initialPhoneNumber
    };
  }

  componentDidMount() {
    // Validate initial value if provided
    if (this.props.value) {
      this.notifyParent();
    }
  }

  detectDefaultCountry = (): string => {
    const language = navigator.language || '';
    const lowerLang = language.toLowerCase();

    // Swiss languages: de-CH, fr-CH, it-CH, or just 'de' (common in Switzerland)
    if (lowerLang.includes('ch') || lowerLang === 'de' || lowerLang === 'de-de') {
      return '+41';
    }

    // French or default to France
    return '+33';
  };

  parsePhoneValue = (value: string): { countryCode: string, phoneNumber: string } => {
    if (!value) return { countryCode: '+33', phoneNumber: '' };

    // Try to parse with libphonenumber-js
    try {
      if (value.startsWith('+')) {
        for (const country of COUNTRIES) {
          if (value.startsWith(country.code)) {
            let phoneNumber = value.substring(country.code.length).trim();

            // Strip leading 0 for French numbers (old format compatibility)
            // National: 06 12 34 56 78 → International: +33 6 12 34 56 78
            if (country.code === '+33' && phoneNumber.startsWith('0')) {
              phoneNumber = phoneNumber.substring(1);
            }

            return {
              countryCode: country.code,
              phoneNumber: phoneNumber
            };
          }
        }
      }
    } catch (e) {
      console.error('Error parsing phone number:', e);
    }

    // If no country code found, assume it's just the number
    return { countryCode: this.state?.countryCode || '+33', phoneNumber: value };
  };

  handleCountryChange = (e: SyntheticEvent<HTMLSelectElement>) => {
    const newCountryCode = e.currentTarget.value;
    this.setState({ countryCode: newCountryCode }, () => {
      this.notifyParent();
    });
  };

  handlePhoneChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const phoneNumber = e.currentTarget.value;
    this.setState({ phoneNumber }, () => {
      this.notifyParent();
    });
  };

  notifyParent = () => {
    const { countryCode, phoneNumber } = this.state;

    // Strip leading 0 for French numbers (for validation with libphonenumber-js)
    let cleanNumber = phoneNumber;
    if (countryCode === '+33' && phoneNumber.startsWith('0')) {
      cleanNumber = phoneNumber.substring(1).trim();
    }

    const fullNumber = cleanNumber ? `${countryCode} ${cleanNumber}` : '';

    // Validate the phone number
    let isValid = false;
    if (fullNumber && phoneNumber.indexOf('_') === -1) {
      const country = COUNTRIES.find(c => c.code === countryCode);
      if (country) {
        try {
          isValid = isValidPhoneNumber(fullNumber, country.iso);
        } catch (e) {
          isValid = false;
        }
      }
    }

    this.props.onChange(fullNumber, countryCode, isValid);
  };

  getCurrentCountry = () => {
    return COUNTRIES.find(c => c.code === this.state.countryCode) || COUNTRIES[0];
  };

  componentDidUpdate(prevProps: Props) {
    // Update internal state if parent value changes externally
    if (prevProps.value !== this.props.value && this.props.value !== `${this.state.countryCode} ${this.state.phoneNumber}`) {
      const parsed = this.parsePhoneValue(this.props.value);
      this.setState({
        countryCode: parsed.countryCode,
        phoneNumber: parsed.phoneNumber
      });
    }
  }

  render() {
    const { placeholder, className, t } = this.props;
    const { countryCode, phoneNumber } = this.state;
    const currentCountry = this.getCurrentCountry();

    return (
      <div className="country-phone-input d-flex">
        <select
          className="form-control form-control-lg country-select"
          value={countryCode}
          onChange={this.handleCountryChange}
          style={{
            maxWidth: '140px',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0
          }}
        >
          {COUNTRIES.map(country => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.code}
            </option>
          ))}
        </select>

        <InputMask
          mask={currentCountry.mask}
          placeholder={placeholder || t('phone.label')}
          maskChar={'_'}
          type="tel"
          className={className || "form-control form-control-lg"}
          style={{
            flex: 1,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0
          }}
          onChange={this.handlePhoneChange}
          value={phoneNumber}
        />
      </div>
    );
  }
}

// $FlowFixMe - withTranslation HOC type inference
export default withTranslation()(CountryPhoneInput);
